<template>
  <v-col class="pa-0">
    <v-row class="ma-0 pa-0">
      <v-col align="center" cols="8" offset="2">
        <h2>{{ $translate('LOGIN_ACCOUNT_CREATION') }}</h2>
      </v-col>
      <v-col v-if="!noCloseButtons" class="text-right">
        <v-icon
          size="20"
          @click="$emit('triggerPopup', { toggle: false, component: 'Login' })"
        >
          ti-close
        </v-icon>
      </v-col>
    </v-row>
    <v-col class="py-0">
      <p class="text-center">{{ $translate('REGISTER_EMAIL_USAGE') }}</p>
    </v-col>
    <v-form v-model="valid" ref="form" lazy-validation>
      <v-row class="mx-3 my-0">
        <v-col cols="12" class="pt-1 pb-0">
          <!-- Email -->
          <v-text-field
            v-model="email"
            :rules="emailRules"
            label="E-mail"
            outlined
            required
            @change="onEmailChange"
          />
          <v-row justify="space-between" class="px-3 my-0">
            <!-- Generate a password -->
            <v-btn
              text
              small
              color="primary"
              class="text-capitalize px-1 primary-font-color"
              @click="generatePassword"
            >
              {{ $translate('GENERATE_PASS') }}
            </v-btn>
            <!-- Copy to clipboard -->
            <!-- <v-btn
              id="copyBtn"
              text
              small
              color="primary"
              class="text-capitalize px-1"
              :disabled="password == null || password.length === 0"
              @click="copyPassword"
            >
              {{ $translate('COPY') }}
            </v-btn> -->
          </v-row>
          <!-- Password -->
          <v-text-field
            v-model="password"
            outlined
            required
            :rules="passwordRules"
            :label="$translate('PASSWORD')"
            :type="hidePassword ? 'password' : 'text'"
            :append-icon="hidePassword ? 'visibility' : 'visibility_off'"
            @click:append="() => (hidePassword = !hidePassword)"
          />
          <!-- Confirm password -->
          <v-text-field
            v-model="passwordConfirm"
            outlined
            required
            :rules="passwordConfirmRules"
            :label="$translate('CONFIRM_PASS')"
            :type="hidePasswordConfirm ? 'password' : 'text'"
            :append-icon="hidePasswordConfirm ? 'visibility' : 'visibility_off'"
            @click:append="() => (hidePasswordConfirm = !hidePasswordConfirm)"
            @keydown.enter="checkEmailForRegister"
          />
          <p class="text-center privacyStatement">
            {{ $translate('AGREE_PRIVACY_STATEMENT') }}
            <a @click="getPrivacyStatement">{{
              $translate('REGISTER_PRIVACY_STATEMENT')
            }}</a
            >.
          </p>
        </v-col>
        <v-col cols="12">
          <v-alert
            v-if="errorMessage !== ''"
            color="red"
            dense
            text
            type="error"
          >
            {{ errorMessage }}
          </v-alert>
          <v-alert
            v-if="infoMessage !== ''"
            color="blue"
            dense
            text
            type="info"
          >
            {{ infoMessage }}
          </v-alert>
          <v-btn
            color="primary"
            depressed
            block
            large
            class="pt-1 primary-font-color"
            @click="onPrimaryBtnClick"
            :loading="inRegisterProcess"
          >
            {{ primaryBtnText }}
          </v-btn>
        </v-col>
        <v-divider />
      </v-row>
    </v-form>
    <v-card v-if="!noCloseButtons" flat color="#d9d9d9" class="mt-3">
      <v-row class="mx-3 my-0 pa-4" align="center" justify="center">
        <v-col align="center" justify="center" class="pa-0">
          <v-btn
            color="primary"
            class="primary-font-color"
            depressed
            large
            @click="$emit('triggerPopup', { toggle: true, component: 'Login' })"
          >
            {{ $translate('BACK') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-col>
</template>

<script>
import doRequest from '@/api/dssRequest';
import UserObject from '@/objects/UserObject';
import uiHelpers from '@/shared/ui_helpers';
import utility from '@/shared/utility';

const BUTTON_REGISTER = 'Register';
const BUTTON_RESEND_ACTIVATION_MAIL = 'ResendActivationMail';

export default {
  name: 'AccountRegister',
  mixins: [uiHelpers],
  props: {
    noCloseButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      email: '',
      emailRules: [
        (v) => !!v || this.$translate('EMAIL_REQUIRED'),
        (v) => /.+@.+\..+/.test(v) || this.$translate('EMAIL_VALID'),
      ],
      valid: null,
      errorMessage: '',
      infoMessage: '',
      password: '',
      passwordConfirm: '',
      passwordRules: [
        (v) => !!v || this.$translate('PASSWORD_REQUIRED'),
        (v) =>
          (v.length >= 8 && v.length <= 48) ||
          this.$translate('PASS_LENGTH_LIMIT'),
      ],
      passwordConfirmRules: [
        (v) =>
          (!!v && v === this.password) ||
          this.$translate('REGISTER_PASS_NOT_MATCH'),
      ],
      hidePassword: true,
      hidePasswordConfirm: true,
      inRegisterProcess: false,
      buttonFor: BUTTON_REGISTER,
    };
  },
  mounted() {},
  computed: {
    userMgrId() {
      return `${this.$store.getters['Account/partnerGroupDetails'].AccountId}_UM`;
    },
    primaryBtnText() {
      switch (this.buttonFor) {
        case BUTTON_RESEND_ACTIVATION_MAIL:
          return this.$translate('RESEND_ACTIVATION');
        case BUTTON_REGISTER:
        default:
          return this.$translate('LOGIN_ACCOUNT_CREATION');
      }
    },
  },
  methods: {
    getPrivacyStatement() {
      this.$emit('triggerPopup', { toggle: false, component: 'Register' });
      this.goToPage('PrivacyStatement');
    },
    register() {
      this.errorMessage = '';
      this.user = new UserObject(this.email);

      this.user.userRegister(
        this.password,
        (response) => {
          this.inRegisterProcess = false;
          this.$emit('triggerPopup', {
            toggle: true,
            component: 'RegisterSuccess',
          });
        },
        (error) => {
          this.inRegisterProcess = false;
          this.errorMessage = this.$translateError(error.data.ErrorCode);
        }
      );
    },
    checkEmailForRegister() {
      this.inRegisterProcess = true;
      this.errorMessage = '';
      this.infoMessage = '';

      if (!this.$refs.form.validate()) {
        this.inRegisterProcess = false;
        return;
      }

      const payload = {
        Email: this.email,
      };

      doRequest(
        this.onCheckEmailResponse,
        (error) => {
          this.inRegisterProcess = false;
          this.errorMessage = this.$translateError(error.data.ErrorCode);
        },
        'checkEmail',
        this.userMgrId,
        payload
      );
    },
    resendActivationMail() {
      this.inRegisterProcess = true;
      this.infoMessage = '';
      this.errorMessage = '';

      const payload = {
        Email: this.email,
      };

      doRequest(
        () => {
          // On response
          this.inRegisterProcess = false;

          this.$emit('triggerPopup', {
            toggle: true,
            component: 'AccountResendActivationSuccess',
          });
        },
        (error) => {
          // On error
          this.inRegisterProcess = false;
          this.errorMessage = this.$translateError(error.data.ErrorCode);
        },
        'resendActivationMail',
        this.userMgrId,
        payload
      );
    },
    onEmailChange() {
      this.buttonFor = BUTTON_REGISTER;
      this.errorMessage = '';
      this.infoMessage = '';
    },
    onPrimaryBtnClick() {
      switch (this.buttonFor) {
        case BUTTON_RESEND_ACTIVATION_MAIL:
          // Resend activation email
          this.resendActivationMail();
          break;
        case BUTTON_REGISTER:
        default:
          // Check email and register
          this.checkEmailForRegister();
      }
    },
    onCheckEmailResponse(response) {
      // To handle the successful response from checkEmail function
      const alreadyExist = response.data.AlreadyPartOfPartnerGroup;
      const userState = response.data.UserState;

      if (userState === 1) {
        // Registered but not activated user
        // Change button text to 'Resend Activation Email',
        // which can resend the activation email to user
        this.inRegisterProcess = false;
        this.buttonFor = BUTTON_RESEND_ACTIVATION_MAIL;
        this.errorMessage = this.$translate('REGISTER_ACCOUNT_NOT_ACTIVATED');
        this.infoMessage = this.$translate(
          'REGISTER_ACCOUNT_RESEND_ACTIVATION_EMAIL'
        );
      } else if (userState === 2) {
        // Registered and activated user, can login directly
        // Show different message based on the flag 'AlreadyPartOfPartnerGroup'
        this.inRegisterProcess = false;

        if (alreadyExist) {
          // User exists in this partner group, let user login directly
          this.errorMessage = this.$translate('REGISTER_ACCOUNT_EXIST_CURR_PG');
        } else {
          // User not exists in this partner group, but exists in other partner groups
          // Show the first partner group that the user belongs to
          // let user login directly using that account
          let pgName = '';

          if (
            response.data.PartnerGroups != null &&
            response.data.PartnerGroups.length > 0
          ) {
            const [partnerGroup] = response.data.PartnerGroups;
            pgName = partnerGroup.PartnerGroupDisplayName;
          }

          this.errorMessage = this.$translate('REGISTER_ACCOUNT_EXIST').replace(
            'PG_NAME',
            pgName
          );
        }
      } else {
        // Non-registered user, continue with registeration
        this.register();
      }
    },
    generatePassword() {
      // Gnerate a password for user
      this.password = utility.getRandomString();
      this.passwordConfirm = this.password;
    },
    copyPassword() {
      utility.copyToClipboard(this.password);
    },
  },
};
</script>

<style scoped>
.privacyStatement {
  font-size: 14px;
}
</style>
